import StyledButton from '@/components/styled/StyledButton';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { useGetAllMySuppliers } from '@/api/supplier.api';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useCreateFlight, useUpdateFlight } from '@/hooks/useFlight';
import { CreateFlightWithTranslationsType } from '@/types/benefits/flight/CreateFlight.type';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  FlightFormDataWithTranslations,
  FlightSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import { useTranslateFields } from '@/hooks/useTranslate';
import StyledStepper from '@/components/styled/StyledStepper';
import { GENERAL_BENEFITS_STEPS } from '@/constants/steppers/generalBenefitsSteps';
import BenefitPricingSummary from '@/components/benefits/BenefitPricingSummary';
import FlightConfiguration from '@/components/flight/FlightConfiguration';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<FlightFormDataWithTranslations>;

const CreateFlightPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const getTranslatedConst = useTranslatedConst();
  const methods = useForm<FlightFormDataWithTranslations>({
    resolver: zodResolver(FlightSchemaWithTranslations),
    mode: 'onChange',
    defaultValues: {
      name: undefined,
      supplier: undefined,
      currency: undefined,
      marginRate: undefined,
      sourceCountry: undefined,
      sourceCity: undefined,
      sourceAirport: undefined,
      destinationCountry: undefined,
      destinationCity: undefined,
      destinationAirport: undefined,
      shortDescription: undefined,
      longDescription: undefined,
      tarifType: 'par-personne',
      customTarif: 'false',
      childAgeFrom: 1,
      childAgeTo: 12,
      tarifs: [
        {
          name: undefined,
          periods: [
            {
              startDate: undefined,
              endDate: undefined,
              applicationDays: [],
            },
          ],
          pricePartitions: [
            {
              price: undefined,
              from: 1,
              to: 1,
            },
          ],
        },
      ],
    },
  });

  const {
    control,
    formState: { isValid, errors },
    handleSubmit,
    getValues,
    setValue,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedSourceCountry = useWatch({
    control,
    name: 'sourceCountry',
  });
  const selectedDestinationCountry = useWatch({
    control,
    name: 'destinationCountry',
  });

  const { data: suppliers } = useGetAllMySuppliers();
  const { mutateAsync: createFlight } = useCreateFlight();
  const { mutateAsync: updateFlight } = useUpdateFlight();

  const [benefitId, setBenefitId] = useState<string | null>(null);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: ['benefit.name', 'shortDescription', 'longDescription'] as const,
  });

  const [currStep, setCurrStep] = useState(0);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      const flight: CreateFlightWithTranslationsType = {
        name: data.name,
        supplierId: data.supplier.value,
        currency: data.currency.value,
        marginRate: parsedMarginRate.success
          ? parsedMarginRate.data
          : undefined,
        sourceCountry: data.sourceCountry?.value,
        sourceCity: data.sourceCity?.value,
        sourceAirport: data.sourceAirport?.value,
        destinationCountry: data.destinationCountry?.value,
        destinationCity: data.destinationCity?.value,
        destinationAirport: data.destinationAirport?.value,
        shortDescription: data.shortDescription,
        longDescription: data.longDescription,
        specialTarif: data.customTarif === 'true',
        childAgeFrom: data.childAgeFrom,
        childAgeTo: data.childAgeTo,
        pricings: data.tarifs.map((tarif) => ({
          name: tarif.name,
          periods: tarif.periods.map((period) => ({
            startDate: period.startDate,
            endDate: period.endDate,
            applicationDays: period.applicationDays,
          })),
          pricePartitions: tarif.pricePartitions.map((partition) => ({
            price: Number(partition.price),
            from: 0,
            to: 0,
            specialPrice: partition.specialPrice
              ? Number(partition.specialPrice)
              : undefined,
          })),
        })),
        status: isDraft ? undefined : SupplierStatus.PUBLISHED,
        translations: data.translations,
      };
      if (benefitId) {
        await toastAsync(updateFlight({ benefitId, data: flight }), {
          success: t('flights.create.update.success'),
        });
      } else {
        const result = await toastAsync(createFlight(flight), {
          success: t('flights.create.success'),
        });
        setBenefitId(result.data.benefitId);
      }
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('flights.create.title')}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('flights.create.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('flights.create.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('flights.create.saveAsDraft')}
            successTitle={t('flights.create.savedSuccess')}
            disabled={
              currentName == undefined ||
              selectedSupplier == undefined ||
              selectedSourceCountry == undefined ||
              selectedDestinationCountry == undefined
            }
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => submitForm(true)(getValues())}
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(GENERAL_BENEFITS_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({ translateToAllLocales: true })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
        />
        {currStep === 0 ? (
          <>
            <FlightConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={true}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('flights.create.saveAsDraft')}
                successTitle={t('flights.create.savedSuccess')}
                onClick={() => submitForm(true)(getValues())}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedSourceCountry == undefined ||
                  selectedDestinationCountry == undefined
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('flights.create.publish')}
                className="font-semibold gap-1 px-3 py-2"
                successTitle={t('flights.create.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('flights.create.requiredFieldsError')}
                onClick={handleSubmit(submitForm(false))}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
              />
            </div>
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <BenefitPricingSummary control={control} />
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
              <StyledButtonLoader
                title={t('flights.create.publish')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={handleSubmit(submitForm(false))}
                successTitle={t('flights.create.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('flights.create.requiredFieldsError')}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
                iconPosition="right"
              />
            </div>
          </>
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateFlightPage;
